// Frontend/src/pages/resident/ResidentSetupPage.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, User, Phone, Lock, Globe, Check } from 'lucide-react';
import { completeProfileSetup, getResidentProfile } from '@/api/residentAuth';
import { useToast } from '@/hooks/use-toast';
import { useLanguage, type Language } from "@/contexts/LanguageContext";
import { PhoneInputField } from "@/components/ui/phone-input";

export default function ResidentSetupPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, setLanguage: setAppLanguage } = useLanguage();
  
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [language, setLanguage] = useState<Language>('es');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState('');
  const [currentEmail, setCurrentEmail] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await getResidentProfile();
      if (response.success) {
        setFullName(response.data.full_name);
        setPhone(response.data.phone || '');
        // First-time setup should default to Spanish pre-login.
        const nextLanguage: Language =
          response.data.has_completed_setup && response.data.language === "en" ? "en" : "es";
        setLanguage(nextLanguage);
        setAppLanguage(nextLanguage);
        setCurrentEmail(response.data.email);
        
        // If already completed setup, redirect to dashboard
        if (response.data.has_completed_setup) {
          navigate('/resident');
        }
      }
    } catch {
      console.warn('Failed to fetch the resident profile.');
      toast({
        title: t("common.error"),
        description: t("resident.setup.profileLoadFailed"),
      });
      navigate('/resident-otp-verify');
    } finally {
      setIsFetching(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!fullName.trim()) {
      setError(t("resident.setup.fullNameRequired"));
      return;
    }
    
    if (!password) {
      setError(t("resident.setup.passwordRequired"));
      return;
    }
    
    if (password.length < 8) {
      setError(t("resident.setup.passwordLength"));
      return;
    }
    
    if (password !== confirmPassword) {
      setError(t("validation.passwordMatch"));
      return;
    }

    setIsLoading(true);

    try {
      const response = await completeProfileSetup({
        full_name: fullName.trim(),
        phone: phone.trim(),
        password,
        language,
      });
      
      if (response.success) {
        toast({
          title: t("resident.setup.completeTitle"),
          description: t("resident.setup.completeDesc"),
        });
        
        // Redirect to resident dashboard
        navigate('/resident');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || t("resident.setup.failed");
      const errors = err.response?.data?.errors;
      
      if (errors) {
        const errorMessages = Object.values(errors).join('. ');
        setError(errorMessages);
      } else {
        setError(errorMessage);
      }
      
      toast({
        title: t("resident.setup.failedTitle"),
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-purple-600" />
          <p className="mt-4 text-gray-600">{t("resident.setup.loadingProfile")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl shadow-2xl">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
            <Check className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
            {t("resident.setup.title")}
          </CardTitle>
          <CardDescription className="text-base">
            {t("resident.setup.subtitle")}
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-5">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>{t("resident.setup.emailLabel")}</strong> {currentEmail}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                {t("resident.setup.emailHint")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-sm font-medium">
                  {t("profile.fullName")} <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      id="fullName"
                      type="text"
                      placeholder={t("resident.setup.fullNamePlaceholder")}
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                    className="pl-10"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium">
                  {t("profile.phone")}
                </Label>
                <div className="relative">
                  <PhoneInputField
                    id="phone"
                    placeholder={t("resident.setup.phonePlaceholder")}
                    value={phone}
                    onChange={(val) => setPhone(val || "")}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="language" className="text-sm font-medium">
                {t("resident.setup.preferredLanguage")}
              </Label>
              <div className="relative">
                <Globe className="absolute left-3 top-3 h-5 w-5 text-gray-400 z-10" />
                <Select
                  value={language}
                  onValueChange={(value) => {
                    const nextLanguage = value === "es" ? "es" : "en";
                    setLanguage(nextLanguage);
                    setAppLanguage(nextLanguage);
                  }}
                  disabled={isLoading}
                >
                  <SelectTrigger className="pl-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">{t("profile.english")}</SelectItem>
                    <SelectItem value="es">{t("profile.spanish")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold mb-4">{t("resident.setup.setPasswordTitle")}</h3>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">
                    {t("auth.password")} <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder={t("resident.setup.passwordPlaceholder")}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                      disabled={isLoading}
                      minLength={8}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium">
                    {t("auth.confirmPassword")} <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder={t("resident.setup.confirmPasswordPlaceholder")}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-3 text-xs text-gray-600 space-y-1">
                <p>• {t("resident.setup.passwordRuleLength")}</p>
                <p>• {t("resident.setup.passwordRuleStrong")}</p>
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-6 text-lg shadow-lg"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {t("resident.setup.completing")}
                </>
              ) : (
                <>
                  <Check className="mr-2 h-5 w-5" />
                  {t("resident.setup.complete")}
                </>
              )}
            </Button>

            <p className="text-center text-xs text-gray-500">
              {t("resident.setup.terms")}
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
